/**
 * MarketWatchPanel — full-featured market watch sidebar panel.
 *
 * All watchlist management lives here (no popup modal needed):
 * - Watchlist selector dropdown (default + user watchlists)
 * - Add symbols via MarketSegmentModal
 * - Remove individual / Remove All symbols
 * - Save / Make Default / Delete / Delete All watchlists
 * - Reconnect WebSocket
 * - Search & sort, drag-to-chart, live LTP, resizable width
 */
import React, { useState, useCallback, useRef, useMemo } from "react";
import { useSelector, useDispatch, useStore } from "react-redux";
import { addPanel, setSymbol, setActiveTab, removePanel } from "../../store/chart-slice";
import { ChartSymbol } from "../../types/chart.types";
import {
  ScripState,
  WatchlistOption,
  addScripsReducer,
  addScripIdsReducer,
  removeScripReducer,
  removeScripIdsReducer,
  removeNewScripReducer,
  removeAllScripsReducer,
  removeAllScripIdsReducer,
  removeAllNewScripsReducer,
  reorderScripsReducer,
  updateSelectedWatchlistReducer,
} from "../../store/watchlist-slice";
import { connectWS, disconnectWS } from "../../service/websocket-handler";
import {
  syncMarketWatchOrder,
  syncMarketWatchScrips,
} from "../../service/market-watch-handler";
import MarketSegmentModal from "../market-segment-modal/MarketSegmentModal";
import SaveAsWatchlistModal from "../market-watch/save-as-watchlist-modal/SaveAsWatchlistModal";
import SaveAsDefaultWatchlistModal from "../market-watch/save-as-default-watchlist-modal/SaveAsDefaultWatchlistModal";
import DeleteWatchlistModal from "../market-watch/delete-watchlist-modal/DeleteWatchlistModal";
import DeleteAllWatchlistModal from "../market-watch/delete-all-watchlist-modal/DeleteAllWatchlistModal";
import Select from "react-select";
import "./MarketWatchPanel.scss";

interface MarketWatchPanelProps {
  onClose: () => void;
  colorTheme: string;
}

type GroupedOption = { label: string; options: WatchlistOption[] };

const MarketWatchPanel: React.FC<MarketWatchPanelProps> = ({ onClose, colorTheme }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const isDark = colorTheme === "dark";

  // ─── Local state ────────────────────────────────────────────────
  const [searchFilter, setSearchFilter] = useState("");
  const [sortField, setSortField] = useState<"symbol" | "ltp">("symbol");
  const [sortAsc, setSortAsc] = useState(true);
  const panelRef = useRef<HTMLDivElement>(null);
  const [resizeWidth, setResizeWidth] = useState(280);
  const resizing = useRef(false);
  const dragItemRef = useRef<number | null>(null);
  const dragOverItemRef = useRef<number | null>(null);

  // Add-symbols modal
  const [addMarketSegmentModalIsOpen, setAddMarketSegmentModalIsOpen] = useState(false);

  // Watchlist actions panel visibility (hidden by default, toggled by gear icon)
  const [showWlActions, setShowWlActions] = useState(false);

  // Watchlist CRUD modals
  const [saveAsUserWatchlistIsOpen, setSaveAsUserWatchlistIsOpen] = useState(false);
  const [saveAsDefaultWatchlistModalIsOpen, setSaveAsDefaultWatchlistModalIsOpen] = useState(false);
  const [deleteWatchlistModalIsOpen, setDeleteWatchlistModalIsOpen] = useState(false);
  const [deleteAllWatchlistModalIsOpen, setDeleteAllWatchlistModalIsOpen] = useState(false);

  // ─── Redux ──────────────────────────────────────────────────────
  const scrips: ScripState[] = useSelector((state: any) => state.watchlist?.scrips || []);
  const panels = useSelector((state: any) => state.chart?.panels || []);
  const selectedWatchlist = useSelector((state: any) => state.watchlist?.selectedWatchlist);
  const { defaultWatchlist = [], userWatchlist = [] } = useSelector(
    (state: any) => state.metadata?.marketWatchState || {}
  );
  const feedsScrips = useSelector(
    (state: any) => state.metadata?.feedsScripsState?.feedsScrips || []
  );

  // ─── Watchlist selector options ─────────────────────────────────
  const defaultWatchlistOptions = useMemo(
    () => defaultWatchlist.map((w: any) => ({ value: w.id, label: w.name, scrips: w.scrips })),
    [defaultWatchlist]
  );
  const userWatchlistOptions = useMemo(
    () => userWatchlist.map((w: any) => ({ value: w.id, label: w.name, scrips: w.scrips })),
    [userWatchlist]
  );
  const isUserWatchlistSelected = useMemo(
    () => userWatchlistOptions.some((o: any) => o.value === selectedWatchlist?.value),
    [selectedWatchlist, userWatchlistOptions]
  );
  const groupedOptions: GroupedOption[] = useMemo(
    () => [
      { label: "Default Watchlist", options: defaultWatchlistOptions },
      { label: "User Watchlist", options: userWatchlistOptions },
    ],
    [defaultWatchlistOptions, userWatchlistOptions]
  );

  // ─── Filter & sort ──────────────────────────────────────────────
  const filtered = scrips
    .filter((s) => {
      if (!searchFilter.trim()) return true;
      const q = searchFilter.trim().toLowerCase();
      return s.symbol.toLowerCase().includes(q) || (s.segment || "").toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (sortField === "symbol") {
        return sortAsc ? a.symbol.localeCompare(b.symbol) : b.symbol.localeCompare(a.symbol);
      }
      const aLtp = a.ltp ?? 0;
      const bLtp = b.ltp ?? 0;
      return sortAsc ? aLtp - bLtp : bLtp - aLtp;
    });

  // ─── Global symbol search ───────────────────────────────────────
  const allSymbols = useMemo(() => {
    const result: { id: number; symbol: string; segment: string }[] = [];
    for (const feed of feedsScrips) {
      for (const s of (feed.scrips || [])) {
        result.push({
          id: typeof s.id === "string" ? parseInt(s.id, 10) : s.id,
          symbol: s.symbol,
          segment: feed.name,
        });
      }
    }
    return result;
  }, [feedsScrips]);

  const watchlistIdSet = useMemo(() => new Set(scrips.map((s) => s.id)), [scrips]);
  const isSearchActive = searchFilter.trim().length >= 1;

  const searchResults = useMemo(() => {
    if (!isSearchActive) return [];
    const q = searchFilter.trim().toLowerCase();
    return allSymbols
      .filter(
        (s) =>
          !watchlistIdSet.has(s.id) &&
          (s.symbol.toLowerCase().includes(q) || s.segment.toLowerCase().includes(q))
      )
      .slice(0, 50);
  }, [isSearchActive, searchFilter, allSymbols, watchlistIdSet]);

  const handleAddFromSearch = useCallback(
    (item: { id: number; symbol: string; segment: string }) => {
      const updatedScrips = [...scrips, { id: item.id, symbol: item.symbol, segment: item.segment, ltp: null }];
      dispatch(addScripsReducer(updatedScrips));
      dispatch(addScripIdsReducer(updatedScrips.map((s) => s.id)));
      syncMarketWatchScrips(updatedScrips.map((s) => s.id));
      setSearchFilter("");
    },
    [scrips, dispatch]
  );

  // ─── Watchlist selection change ─────────────────────────────────
  const handleSelectChange = useCallback(
    (selectedOption: WatchlistOption | null) => {
      const newScrips = feedsScrips
        .flatMap((feed: any) =>
          feed.scrips.map((scrip: any) => ({ ...scrip, segment: feed.name }))
        )
        .filter((scrip: any) => selectedOption?.scrips.includes(scrip.id))
        .map((scrip: any) => ({ symbol: scrip.symbol, id: scrip.id, segment: scrip.segment }));

      dispatch(updateSelectedWatchlistReducer(selectedOption));
      dispatch(removeAllNewScripsReducer());
      dispatch(removeAllScripIdsReducer());
      dispatch(addScripsReducer(newScrips));
      dispatch(addScripIdsReducer(newScrips.map((s: any) => s.id)));
      // Switching template replaces the whole panel, so the stored list must
      // follow it — otherwise the next login would restore a stale panel.
      syncMarketWatchScrips(newScrips.map((s: any) => s.id));
    },
    [dispatch, feedsScrips]
  );

  // ─── Remove single / all ───────────────────────────────────────
  const handleRemove = useCallback(
    (id: number) => {
      // Find the scrip's symbol string before removing it from state
      const removedScrip = scrips.find((s) => s.id === id);
      dispatch(removeScripReducer({ id }));
      dispatch(removeScripIdsReducer({ id }));
      dispatch(removeNewScripReducer({ id }));
      syncMarketWatchScrips(
        scrips.filter((s) => s.id !== id).map((s) => s.id)
      );
      // Close any chart panels currently showing this symbol (match by id OR symbol string)
      panels.forEach((panel: any) => {
        const matchById = panel.symbol?.id === id;
        const matchBySymbol = removedScrip && panel.symbol?.symbol === removedScrip.symbol;
        if (matchById || matchBySymbol) {
          dispatch(removePanel(panel.id));
        }
      });
    },
    [dispatch, panels, scrips]
  );

  const handleRemoveAll = useCallback(() => {
    dispatch(removeAllScripsReducer());
    dispatch(removeAllScripIdsReducer());
    dispatch(removeAllNewScripsReducer());
    syncMarketWatchScrips([]);
  }, [dispatch]);

  // ─── Open in new chart pane ─────────────────────────────────────
  const handleOpenChart = useCallback(
    (scrip: ScripState) => {
      const sym: ChartSymbol = {
        symbol: scrip.symbol, segment: scrip.segment, name: scrip.symbol, id: scrip.id,
      };
      const emptyPanel = panels.find((p: any) => !p.symbol);
      if (emptyPanel) {
        dispatch(setSymbol({ panelId: emptyPanel.id, symbol: sym }));
        dispatch(setActiveTab(emptyPanel.id));
        return;
      }
      dispatch(addPanel());
      setTimeout(() => {
        const state = store.getState() as any;
        const allPanels = state.chart?.panels || [];
        const newest = allPanels[allPanels.length - 1];
        if (newest) dispatch(setSymbol({ panelId: newest.id, symbol: sym }));
      }, 50);
    },
    [dispatch, store, panels]
  );

  // ─── Drag-to-chart ─────────────────────────────────────────────
  const handleDragStart = useCallback(
    (e: React.DragEvent, scrip: ScripState, index: number) => {
      dragItemRef.current = index;
      const sym: ChartSymbol = {
        symbol: scrip.symbol, segment: scrip.segment, name: scrip.symbol, id: scrip.id,
      };
      const payload = JSON.stringify(sym);
      e.dataTransfer.setData("application/chart-symbol", payload);
      e.dataTransfer.setData("text/plain", `chart-symbol:${payload}`);
      e.dataTransfer.effectAllowed = "copyMove";
      (window as any).__chartSymbolDrag = true;
      (window as any).__chartSymbolData = payload;
      window.dispatchEvent(new Event("chart-symbol-drag-start"));
    },
    []
  );

  const handleDragEnter = useCallback((index: number) => {
    dragOverItemRef.current = index;
  }, []);

  const handleDragEnd = useCallback(() => {
    (window as any).__chartSymbolDrag = false;
    (window as any).__chartSymbolData = null;
    window.dispatchEvent(new Event("chart-symbol-drag-end"));
    if (
      dragItemRef.current !== null &&
      dragOverItemRef.current !== null &&
      dragItemRef.current !== dragOverItemRef.current
    ) {
      const fromScrip = filtered[dragItemRef.current];
      const toScrip = filtered[dragOverItemRef.current];
      const fromIndex = scrips.findIndex((s) => s.id === fromScrip?.id);
      const toIndex = scrips.findIndex((s) => s.id === toScrip?.id);
      if (fromIndex !== -1 && toIndex !== -1) {
        dispatch(reorderScripsReducer({ fromIndex, toIndex }));
        // Order is user-meaningful and has no delta form, so persist the whole
        // sequence straight from the state the reducer just produced.
        const nextOrder = (store.getState() as any).watchlist.scrips.map(
          (s: ScripState) => s.id
        );
        syncMarketWatchOrder(nextOrder);
      }
    }
    dragItemRef.current = null;
    dragOverItemRef.current = null;
  }, [dispatch, filtered, scrips, store]);

  // ─── Column sort toggle ─────────────────────────────────────────
  const handleSort = (field: "symbol" | "ltp") => {
    if (sortField === field) setSortAsc(!sortAsc);
    else { setSortField(field); setSortAsc(true); }
  };

  // ─── Resize handle ─────────────────────────────────────────────
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    resizing.current = true;
    const startX = e.clientX;
    const startW = resizeWidth;
    const onMove = (ev: MouseEvent) => {
      if (!resizing.current) return;
      setResizeWidth(Math.max(220, Math.min(500, startW + (ev.clientX - startX))));
    };
    const onUp = () => {
      resizing.current = false;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [resizeWidth]);

  // ─── Reconnect WS ──────────────────────────────────────────────
  const handleReconnect = useCallback(() => {
    disconnectWS();
    connectWS();
  }, []);

  const fmtLtp = (ltp?: number | null) => {
    if (ltp == null) return "—";
    return ltp.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // ─── react-select compact styles ───────────────────────────────
  const selectStyles = useMemo(() => ({
    control: (p: any, s: any) => ({
      ...p,
      backgroundColor: isDark ? "#1e222d" : "#fff",
      borderColor: s.isFocused ? "#3b82f6" : isDark ? "#2a2e39" : "#d0d4dc",
      boxShadow: s.isFocused ? "0 0 0 1px #3b82f6" : "none",
      minHeight: 28, fontSize: 11,
      "&:hover": { borderColor: "#3b82f6" },
    }),
    menu: (p: any) => ({
      ...p,
      backgroundColor: isDark ? "#1e222d" : "#fff",
      border: isDark ? "1px solid #2a2e39" : "1px solid #d0d4dc",
      zIndex: 10002, fontSize: 11,
    }),
    menuList: (p: any) => ({ ...p, padding: 2 }),
    option: (p: any, s: any) => ({
      ...p, fontSize: 11, padding: "4px 8px",
      backgroundColor: s.isSelected ? "#3b82f6" : s.isFocused ? (isDark ? "#2d2d44" : "#f1f5f9") : "transparent",
      color: s.isSelected ? "#fff" : isDark ? "#d1d4dc" : "#1e293b",
      cursor: "pointer",
    }),
    singleValue: (p: any) => ({ ...p, color: isDark ? "#d1d4dc" : "#1e293b" }),
    placeholder: (p: any) => ({ ...p, color: "#787b86" }),
    input: (p: any) => ({ ...p, color: isDark ? "#d1d4dc" : "#1e293b", margin: 0, padding: 0 }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (p: any) => ({ ...p, padding: "0 4px", color: isDark ? "#787b86" : "#64748b" }),
    groupHeading: (p: any) => ({
      ...p, fontSize: 9, fontWeight: 700, textTransform: "uppercase",
      color: isDark ? "#787b86" : "#64748b", letterSpacing: "0.5px",
    }),
  }), [isDark]);

  const formatOptionLabel = (option: WatchlistOption) => (
    <span>{option.label} ({option.scrips?.length ?? 0})</span>
  );

  const formatGroupLabel = (data: GroupedOption) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span>{data.label}</span>
      <span style={{ background: "#3b82f6", color: "#fff", borderRadius: 10, fontSize: 9, padding: "1px 6px" }}>
        {data.options.length}
      </span>
    </div>
  );

  return (
    <div ref={panelRef} className={`mw-panel ${colorTheme}`} style={{ width: resizeWidth }}>
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="mw-header">
        <span className="mw-title">MARKET WATCH</span>
        <div className="mw-header-actions">
          <button className="mw-reconnect-badge" onClick={handleReconnect}>
            Reconnect
          </button>
          <button className="mw-close-btn" onClick={onClose} title="Close Market Watch">
            <svg viewBox="0 0 20 20" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="4" y1="4" x2="16" y2="16" />
              <line x1="16" y1="4" x2="4" y2="16" />
            </svg>
          </button>
        </div>
      </div>

      {/* ── Watchlist selector row ─────────────────────────────── */}
      <div className="mw-watchlist-row">
        <span className="mw-watchlist-label">Watchlist</span>
        <div className="mw-watchlist-selector">
          <Select<WatchlistOption, false, GroupedOption>
            placeholder="Select Watchlist"
            menuPlacement="auto"
            options={groupedOptions}
            value={selectedWatchlist}
            formatGroupLabel={formatGroupLabel}
            formatOptionLabel={formatOptionLabel}
            styles={selectStyles}
            onChange={handleSelectChange}
            classNamePrefix="mw-select"
          />
        </div>
        <button
          className={`mw-wl-settings-btn${showWlActions ? " active" : ""}`}
          onClick={() => setShowWlActions(v => !v)}
          title="Watchlist settings"
        >
          <svg viewBox="0 0 20 20" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="10" cy="10" r="2.5" />
            <path d="M10 2v1.5M10 16.5V18M2 10h1.5M16.5 10H18M4.22 4.22l1.06 1.06M14.72 14.72l1.06 1.06M4.22 15.78l1.06-1.06M14.72 5.28l1.06-1.06" />
          </svg>
        </button>
      </div>

      {/* ── Watchlist action buttons (hidden by default, shown via gear icon) ── */}
      {showWlActions && <div className="mw-wl-actions">
        <button
          className="mw-wl-btn save"
          disabled={!filtered.length}
          onClick={() => setSaveAsUserWatchlistIsOpen(true)}
          title="Save as watchlist"
        >Save</button>
        <button
          className="mw-wl-btn default"
          disabled={!selectedWatchlist}
          onClick={() => setSaveAsDefaultWatchlistModalIsOpen(true)}
          title="Make default"
        >Default</button>
        <button
          className="mw-wl-btn delete"
          disabled={!(selectedWatchlist && isUserWatchlistSelected)}
          onClick={() => setDeleteWatchlistModalIsOpen(true)}
          title="Delete watchlist"
        >Delete</button>
        <button
          className="mw-wl-btn delete-all"
          disabled={!userWatchlistOptions.length}
          onClick={() => setDeleteAllWatchlistModalIsOpen(true)}
          title="Delete all user watchlists"
        >Del All</button>
      </div>}

      {/* ── Toolbar: Add / Search / Remove All / Count ─────────── */}
      <div className="mw-toolbar">
        <button
          className="mw-add-btn"
          onClick={() => setAddMarketSegmentModalIsOpen(true)}
          title="Add symbols"
        >
          <svg viewBox="0 0 20 20" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="10" y1="4" x2="10" y2="16" /><line x1="4" y1="10" x2="16" y2="10" />
          </svg>
          <span>Add</span>
        </button>
        <input
          type="text"
          className="mw-search-input"
          placeholder="Search..."
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
        />
        <button
          className="mw-rmall-btn"
          disabled={!filtered.length}
          onClick={handleRemoveAll}
          title="Remove all symbols"
        >
          <svg viewBox="0 0 20 20" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 17 6" />
            <path d="M6 6V4a2 2 0 012-2h4a2 2 0 012 2v2m2 0v10a2 2 0 01-2 2H6a2 2 0 01-2-2V6h12z" />
          </svg>
        </button>
      </div>

      {/* ── Column headers + list OR search results ────────────── */}
      {isSearchActive ? (
        <div className="mw-search-panel">
          <div className="mw-search-panel-header">
            <span>Search Results</span>
            <span className="mw-search-count">{searchResults.length} found</span>
          </div>
          {searchResults.length === 0 ? (
            <div className="mw-search-empty">No symbols found for &ldquo;{searchFilter}&rdquo;</div>
          ) : (
            <div className="mw-search-list">
              {searchResults.map((item) => (
                <div
                  key={`${item.id}-${item.segment}`}
                  className="mw-search-item"
                  onClick={() => handleAddFromSearch(item)}
                >
                  <div className="mw-search-item-info">
                    <span className="mw-search-symbol">{item.symbol}</span>
                    <span className="mw-search-segment">{item.segment}</span>
                  </div>
                  <button className="mw-search-add-btn" onClick={(e) => { e.stopPropagation(); handleAddFromSearch(item); }}>
                    <svg viewBox="0 0 20 20" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <line x1="10" y1="4" x2="10" y2="16" /><line x1="4" y1="10" x2="16" y2="10" />
                    </svg>
                    <span>Add</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
      {/* ── Column headers ─────────────────────────────────────── */}
      <div className="mw-col-headers">
        <span className="mw-col mw-col-symbol" onClick={() => handleSort("symbol")}>
          Symbol ({filtered.length}) {sortField === "symbol" ? (sortAsc ? "▲" : "▼") : ""}
        </span>
        <span className="mw-col mw-col-ltp" onClick={() => handleSort("ltp")}>
          LTP {sortField === "ltp" ? (sortAsc ? "▲" : "▼") : ""}
        </span>
        <span className="mw-col mw-col-action"></span>
      </div>

      {/* ── Symbols list ───────────────────────────────────────── */}
      <div className="mw-list">
        {filtered.length === 0 && (
          <div className="mw-empty">
            {scrips.length === 0 ? "No symbols in watchlist" : "No matches"}
          </div>
        )}
        {filtered.map((scrip, idx) => (
          <div
            key={scrip.id}
            className="mw-row"
            draggable
            onDragStart={(e) => handleDragStart(e, scrip, idx)}
            onDragEnter={() => handleDragEnter(idx)}
            onDragOver={(e) => e.preventDefault()}
            onDragEnd={handleDragEnd}
            title={`Drag to reorder or drop on chart\n${scrip.symbol} · ${scrip.segment || ""}`}
          >
            <span className="mw-cell mw-cell-drag" title="Drag to reorder">⠿</span>
            <span className="mw-cell mw-cell-symbol">{scrip.symbol}</span>
            <span className={`mw-cell mw-cell-ltp ${scrip.ltpDirection === 'up' ? 'ltp-up' : scrip.ltpDirection === 'down' ? 'ltp-down' : ''}`}>
              {fmtLtp(scrip.ltp)}
            </span>
            <button
              className="mw-cell mw-cell-chart-btn"
              onClick={() => handleOpenChart(scrip)}
              title="Open in chart"
            >
              <svg viewBox="0 0 20 20" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="16" height="16" rx="2" />
                <polyline points="5,14 9,8 12,11 15,6" />
              </svg>
            </button>
            <button
              className="mw-cell mw-cell-remove-btn"
              onClick={() => handleRemove(scrip.id)}
              title="Remove symbol"
            >
              <svg viewBox="0 0 20 20" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="5" y1="5" x2="15" y2="15" /><line x1="15" y1="5" x2="5" y2="15" />
              </svg>
            </button>
          </div>
        ))}
      </div>
        </>
      )}

      {/* ── Resize handle ──────────────────────────────────────── */}
      <div className="mw-resize-handle" onMouseDown={handleResizeStart} />

      {/* ── Modals (rendered portals) ──────────────────────────── */}
      <MarketSegmentModal
        addMarketSegmentModalIsOpen={addMarketSegmentModalIsOpen}
        setAddMarketSegmentModalIsOpen={setAddMarketSegmentModalIsOpen}
      />
      <SaveAsWatchlistModal
        saveAsUserWatchlistIsOpen={saveAsUserWatchlistIsOpen}
        setSaveAsUserWatchlistIsOpen={setSaveAsUserWatchlistIsOpen}
      />
      <SaveAsDefaultWatchlistModal
        saveAsDefaultWatchlistModalIsOpen={saveAsDefaultWatchlistModalIsOpen}
        setSaveAsDefaultWatchlistModalIsOpen={setSaveAsDefaultWatchlistModalIsOpen}
      />
      <DeleteWatchlistModal
        deleteWatchlistModalIsOpen={deleteWatchlistModalIsOpen}
        setDeleteWatchlistModalIsOpen={setDeleteWatchlistModalIsOpen}
      />
      <DeleteAllWatchlistModal
        deleteAllWatchlistModalIsOpen={deleteAllWatchlistModalIsOpen}
        setDeleteAllWatchlistModalIsOpen={setDeleteAllWatchlistModalIsOpen}
      />
    </div>
  );
};

export default MarketWatchPanel;
